/**
 * Governance Policies Page
 * 
 * Displays and manages governance configuration:
 * - Signature thresholds (normal, critical)
 * - Timelocks (deploy, upgrade, emergency)
 * - Action cooldowns
 * - Fail-closed rules
 * 
 * Part of #259 Navigation Restructure
 */

import { useState, useEffect } from 'react';
import { useDebug } from '../context/DebugContext';
import './Policies.css';

// SVG Icons
const Icons = {
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l9 4v6c0 5.5-3.5 10-9 12-5.5-2-9-6.5-9-12V6l9-4z" />
    </svg>
  ),
  Clock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Lock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  ),
  AlertTriangle: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 4L6 11l-3-3" />
    </svg>
  ),
};

interface GovernancePolicy {
  // Thresholds
  normalThreshold: number;
  criticalThreshold: number;
  keyHolderCount: number;
  
  // Timelocks (in minutes)
  deployTimelock: number;
  upgradeTimelock: number;
  emergencyTimelock: number;
  
  // Cooldowns (in minutes)
  actionCooldown: number;
  proposalCooldown: number;
  
  // Fail-closed rules
  failedDeployThreshold: number;
  nodeFailureThreshold: number;
  requireCriticalForRecovery: boolean;
}

interface PolicyAction {
  action: string;
  label: string;
  threshold: 'normal' | 'critical';
  timelock: string;
}

const GOVERNANCE_ACTIONS: PolicyAction[] = [
  { action: 'governance.node.deploy', label: 'Deploy Node', threshold: 'normal', timelock: 'deploy' },
  { action: 'governance.node.decommission', label: 'Decommission Node', threshold: 'normal', timelock: 'deploy' },
  { action: 'governance.release.deploy', label: 'Deploy Release', threshold: 'normal', timelock: 'deploy' },
  { action: 'governance.circuit.deploy', label: 'Deploy Circuit', threshold: 'normal', timelock: 'deploy' },
  { action: 'governance.config.update', label: 'Update Config', threshold: 'normal', timelock: 'deploy' },
  { action: 'governance.key.add', label: 'Add Key Holder', threshold: 'critical', timelock: 'upgrade' },
  { action: 'governance.key.rotate', label: 'Rotate Key', threshold: 'critical', timelock: 'upgrade' },
  { action: 'governance.key.remove', label: 'Remove Key Holder', threshold: 'critical', timelock: 'upgrade' },
  { action: 'governance.threshold.update', label: 'Update Thresholds', threshold: 'critical', timelock: 'upgrade' },
  { action: 'governance.emergency.halt', label: 'Emergency Halt', threshold: 'critical', timelock: 'emergency' },
  { action: 'governance.emergency.resume', label: 'Resume Operations', threshold: 'critical', timelock: 'upgrade' },
];

export default function Policies() {
  const { addEvent } = useDebug();
  const [policy, setPolicy] = useState<GovernancePolicy | null>(null);
  const [loading, setLoading] = useState(true);
  // TODO: Implement edit mode for policy changes
  // const [editMode, setEditMode] = useState<string | null>(null);
  
  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Governance Policies',
    });
    
    fetchPolicy();
  }, [addEvent]);
  
  const fetchPolicy = async () => {
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      const response = await fetch(`${edgeUrl}/api/governance/config`);
      if (response.ok) {
        const data = await response.json();
        setPolicy({
          normalThreshold: data.normalThreshold || 2,
          criticalThreshold: data.criticalThreshold || 4,
          keyHolderCount: data.keyHolderCount || 5,
          deployTimelock: data.deployTimelock || 60,
          upgradeTimelock: data.upgradeTimelock || 4320, // 72 hours
          emergencyTimelock: data.emergencyTimelock || 5,
          actionCooldown: data.actionCooldown || 15,
          proposalCooldown: data.proposalCooldown || 60,
          failedDeployThreshold: data.failedDeployThreshold || 3,
          nodeFailureThreshold: data.nodeFailureThreshold || 50,
          requireCriticalForRecovery: data.requireCriticalForRecovery ?? true,
        });
      } else {
        // Default policy for display
        setPolicy({
          normalThreshold: 2,
          criticalThreshold: 4,
          keyHolderCount: 5,
          deployTimelock: 60,
          upgradeTimelock: 4320,
          emergencyTimelock: 5,
          actionCooldown: 15,
          proposalCooldown: 60,
          failedDeployThreshold: 3,
          nodeFailureThreshold: 50,
          requireCriticalForRecovery: true,
        });
      }
    } catch {
      // Default policy
      setPolicy({
        normalThreshold: 2,
        criticalThreshold: 4,
        keyHolderCount: 5,
        deployTimelock: 60,
        upgradeTimelock: 4320,
        emergencyTimelock: 5,
        actionCooldown: 15,
        proposalCooldown: 60,
        failedDeployThreshold: 3,
        nodeFailureThreshold: 50,
        requireCriticalForRecovery: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const formatTimelock = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.round(minutes / 60)} hours`;
    return `${Math.round(minutes / 1440)} days`;
  };
  
  const getTimelockValue = (timelockType: string): number => {
    if (!policy) return 0;
    switch (timelockType) {
      case 'deploy': return policy.deployTimelock;
      case 'upgrade': return policy.upgradeTimelock;
      case 'emergency': return policy.emergencyTimelock;
      default: return 0;
    }
  };
  
  if (loading) {
    return (
      <div className="policies-page">
        <div className="loading-state">
          <div className="spinner spinner-lg" />
          <p>Loading governance policies...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="policies-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Governance Policies</h1>
          <p>Signature thresholds, timelocks, and fail-closed rules configured at genesis</p>
        </div>
        <button className="btn-primary" disabled>
          <Icons.Edit /> Propose Changes
        </button>
      </div>
      
      {/* Thresholds Section */}
      <section className="policy-section">
        <div className="section-header">
          <Icons.Shield />
          <h2>Signature Thresholds</h2>
        </div>
        
        <div className="policy-grid">
          <div className="policy-card">
            <div className="policy-label">Normal Threshold</div>
            <div className="policy-value">
              {policy?.normalThreshold} of {policy?.keyHolderCount}
            </div>
            <div className="policy-description">
              Required for routine operations like deployments and config updates
            </div>
            <div className="policy-progress">
              <div 
                className="progress-bar normal"
                style={{ width: `${((policy?.normalThreshold || 0) / (policy?.keyHolderCount || 1)) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Critical Threshold</div>
            <div className="policy-value">
              {policy?.criticalThreshold} of {policy?.keyHolderCount}
            </div>
            <div className="policy-description">
              Required for key rotation, protocol upgrades, and emergency actions
            </div>
            <div className="policy-progress">
              <div 
                className="progress-bar critical"
                style={{ width: `${((policy?.criticalThreshold || 0) / (policy?.keyHolderCount || 1)) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Total Key Holders</div>
            <div className="policy-value">{policy?.keyHolderCount}</div>
            <div className="policy-description">
              Active signers in the multi-sig governance
            </div>
          </div>
        </div>
      </section>
      
      {/* Timelocks Section */}
      <section className="policy-section">
        <div className="section-header">
          <Icons.Clock />
          <h2>Timelocks</h2>
        </div>
        
        <div className="policy-grid">
          <div className="policy-card">
            <div className="policy-label">Deploy Timelock</div>
            <div className="policy-value">{formatTimelock(policy?.deployTimelock || 0)}</div>
            <div className="policy-description">
              Delay before deployments execute after approval
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Upgrade Timelock</div>
            <div className="policy-value">{formatTimelock(policy?.upgradeTimelock || 0)}</div>
            <div className="policy-description">
              Delay for protocol upgrades and key changes
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Emergency Timelock</div>
            <div className="policy-value">{formatTimelock(policy?.emergencyTimelock || 0)}</div>
            <div className="policy-description">
              Fast-track for emergency halt (critical threshold still required)
            </div>
          </div>
        </div>
      </section>
      
      {/* Cooldowns Section */}
      <section className="policy-section">
        <div className="section-header">
          <Icons.Lock />
          <h2>Cooldowns</h2>
        </div>
        
        <div className="policy-grid half">
          <div className="policy-card">
            <div className="policy-label">Action Cooldown</div>
            <div className="policy-value">{formatTimelock(policy?.actionCooldown || 0)}</div>
            <div className="policy-description">
              Minimum time between consecutive governance actions
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Proposal Cooldown</div>
            <div className="policy-value">{formatTimelock(policy?.proposalCooldown || 0)}</div>
            <div className="policy-description">
              Time before a rejected proposal can be resubmitted
            </div>
          </div>
        </div>
      </section>
      
      {/* Fail-Closed Rules */}
      <section className="policy-section">
        <div className="section-header">
          <Icons.AlertTriangle />
          <h2>Fail-Closed Rules</h2>
        </div>
        
        <div className="policy-grid">
          <div className="policy-card">
            <div className="policy-label">Failed Deploy Threshold</div>
            <div className="policy-value">{policy?.failedDeployThreshold} consecutive</div>
            <div className="policy-description">
              Automatic halt after this many failed deployments
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Node Failure Threshold</div>
            <div className="policy-value">{policy?.nodeFailureThreshold}%</div>
            <div className="policy-description">
              Automatic halt when this percentage of nodes fail
            </div>
          </div>
          
          <div className="policy-card">
            <div className="policy-label">Recovery Mode</div>
            <div className="policy-value">
              {policy?.requireCriticalForRecovery ? 'Critical Threshold' : 'Normal Threshold'}
            </div>
            <div className="policy-description">
              Threshold required to resume after fail-closed halt
            </div>
          </div>
        </div>
      </section>
      
      {/* Action Matrix */}
      <section className="policy-section">
        <div className="section-header">
          <Icons.Check />
          <h2>Action Requirements Matrix</h2>
        </div>
        
        <div className="action-matrix">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Threshold</th>
                <th>Timelock</th>
              </tr>
            </thead>
            <tbody>
              {GOVERNANCE_ACTIONS.map(action => (
                <tr key={action.action}>
                  <td>
                    <span className="action-name">{action.label}</span>
                    <span className="action-id">{action.action}</span>
                  </td>
                  <td>
                    <span className={`threshold-badge ${action.threshold}`}>
                      {action.threshold === 'normal' 
                        ? `${policy?.normalThreshold}/${policy?.keyHolderCount}` 
                        : `${policy?.criticalThreshold}/${policy?.keyHolderCount}`}
                    </span>
                  </td>
                  <td>{formatTimelock(getTimelockValue(action.timelock))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      
      {/* Genesis Info */}
      <div className="genesis-note">
        <Icons.Shield />
        <p>
          These policies were set during network genesis and require a critical threshold 
          ({policy?.criticalThreshold}/{policy?.keyHolderCount}) with upgrade timelock 
          ({formatTimelock(policy?.upgradeTimelock || 0)}) to modify.
        </p>
      </div>
    </div>
  );
}
